import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, throwError as observableThrowError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthService } from '../../auth/services/auth.service';
import { HttpResponseHandlerService } from '../../auth/services/http-response-handler.service';
import { SecurityCacheService } from '../../auth/services/security-cache.service';
import { appInjector } from '../app-injector';
import { AppSettings } from '../settings/app-settings.model';
import { getResult } from './response-handlers.model';

export abstract class BaseService<T extends AppSettings.IWebServiceSettings> {
    protected http: HttpClient;
    protected settings: AppSettings;
    protected serviceSettings: T;
    protected serviceUrl: string;
    protected resourceIdentifier: string;
    protected authService: AuthService;
    protected httpResponseHandlerService: HttpResponseHandlerService;

    constructor(selector: (appSetings: AppSettings) => T) {
        var injector = appInjector();
        this.httpResponseHandlerService = <HttpResponseHandlerService>injector.get(HttpResponseHandlerService);
        this.http = <HttpClient>injector.get(HttpClient);
        this.settings = <AppSettings>injector.get(AppSettings);
        this.authService = <AuthService>injector.get(AuthService);
        this.serviceSettings = selector(this.settings);
        this.serviceUrl = (this.serviceSettings && this.serviceSettings.serviceUrl || null);
        this.resourceIdentifier = (this.settings && this.settings.security && this.settings.security.resourceIdentifier || null);
    }

    private appendTimeStamp(path: string): string {
        // avoid caching get methods by passing timestamp
        const d = new Date().getTime();
        if (path.indexOf('?') > 0) {
            return path + '&' + d;
        } else {
            return path + '?' + d;
        }
    }

    protected get(path: string, contentType: string = 'application/json'): Observable<any> {
        return this.getAbsolute(this.serviceUrl + path, contentType);
    }

    protected getAbsolute = (path: string, contentType: string = 'application/json'): Observable<any> => {
        if (this.authService.hasApplicationAccess) {
            let headers = this.getHeaders(
                { 'Accept': contentType }
            );
            if (contentType == 'application/json') {
                headers = headers.set('Cache-Control', 'private, no-store');
                return this.http
                    .get(path, { headers: headers, observe: 'body' }).pipe(
                        catchError(err => this.handleHttpError(err)));
            } else if (contentType == 'image/jpeg' || contentType == 'image/png') {
                return this.http
                    .get(this.appendTimeStamp(path), { headers: headers, observe: 'response', responseType: 'blob' }).pipe(
                        catchError(err => this.handleHttpError(err)))
                    .pipe(map(res => res));
            } else {
                return this.http
                    .get(this.appendTimeStamp(path), { headers: headers, observe: 'response', responseType: 'text' }).pipe(
                        catchError(err => this.handleHttpError(err)))
                    .pipe(map(res => res));
            }
        }
    };

    protected getMaxResult(url: string) {
        return getResult(this.get(url));
    }

    protected put = (path: string, content: any, replacer?: (key: string, value: any) => any, contentType: string = 'application/json'): Observable<any> => {
        if (this.authService.hasApplicationAccess) {
            const headers = this.getHeaders({
                'Accept': contentType,
                'Content-Type': 'application/json'
            });
            if (contentType == 'application/json') {
                return this.http
                    .put(this.serviceUrl + path, JSON.stringify(content, replacer), { headers: headers, observe: 'body' }).pipe(
                        catchError(err => this.handleHttpError(err)));
            } else {
                return this.http
                    .put(this.serviceUrl + path, JSON.stringify(content, replacer), { headers: headers, observe: 'body' }).pipe(
                        catchError(err => this.handleHttpError(err)))
                    .pipe(map(res => res));
            }
        }
    };

    protected post = (path: string, content: any, replacer?: (key: string, value: any) => any, contentType: string = 'application/json'): Observable<any> => {
        if (this.authService.hasApplicationAccess) {
            const headers = this.getHeaders({
                'Accept': contentType,
                'Content-Type': 'application/json'
            });
            if (contentType == 'application/json') {
                return this.http
                    .post(this.serviceUrl + path, JSON.stringify(content, replacer), { headers: headers, observe: 'body' }).pipe(
                        catchError(err => this.handleHttpError(err)));
            } else {
                return this.http
                    .post(this.serviceUrl + path, JSON.stringify(content, replacer), { headers: headers, observe: 'response' }).pipe(
                        catchError(err => this.handleHttpError(err)))
                    .pipe(map(res => res));
            }
        }
    };

    protected delete = (path: string): Observable<any> => {
        if (this.authService.hasApplicationAccess) {
            const headers = this.getHeaders({
                'Accept': 'application/json'
            });
            return this.http
                .delete(this.serviceUrl + path, { headers: headers, observe: 'body' }).pipe(
                    catchError(err => this.handleHttpError(err)));
        }
    };

    protected stripEmptyValues = (key: string, value: any): string => value === ''
        ? undefined
        : value;

    protected getHeaders = (headers: { [key: string]: any }): HttpHeaders => {
        headers = headers || {};
        // do not add header for omega service
        if (this.settings.omega.serviceUrl != this.serviceUrl) {
            const businessUnit = SecurityCacheService.businessUnitId;
            if (!headers[this.authService.businessUnitHeaderName] && businessUnit) {
                headers[this.authService.businessUnitHeaderName] = `${businessUnit}`;
            }
            const securityStamp = SecurityCacheService.securityStamp;
            if (!headers[this.authService.securityStampHeaderName] && securityStamp) {
                headers[this.authService.securityStampHeaderName] = `${securityStamp}`;
            }
            const windowId = SecurityCacheService.window;
            if (!headers[this.authService.windowHeaderName] && windowId != '' && windowId != null) {
                headers[this.authService.windowHeaderName] = windowId;
            }
        }
        return new HttpHeaders(headers);
    };

    protected handleHttpError = (err: HttpErrorResponse): Observable<any> => {
        this.httpResponseHandlerService.handleServiceStatus(err.status, err.error);
        return observableThrowError(err);
    };
}
